import { useCallback } from 'react'
import type { ChatMessage } from '../../shared/types/chat'
import { formatDisplayTime } from '@tendbot/ui'
import * as api from './api'

/**
 * Detects decline-intent messages and locally updates pending approvals.
 * Returns a function: `(content: string) => boolean` — true if the message
 * was handled as a decline, false if normal send should proceed.
 */
export function useDeclineHandler(
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
): (content: string) => boolean {
  return useCallback((content: string): boolean => {
    const lower = content.toLowerCase().trim()
    const declineWords = /\b(decline|deny|reject|refuse|cancel)\b/
    const targetWords = /\b(them|those|all|approval|approvals|pending|it|the)\b/
    if (!(declineWords.test(lower) && (targetWords.test(lower) || lower.split(/\s+/).length <= 3))) {
      return false
    }

    let handled = false
    setMessages(prev => {
      const pendingMsgs = prev.filter(m => m.approval?.status === 'pending')
      if (pendingMsgs.length === 0) return prev

      handled = true
      const updated = prev.map(m => {
        if (m.approval?.status === 'pending') {
          return { ...m, approval: { ...m.approval, status: 'denied' as const } }
        }
        return m
      })

      const titles = pendingMsgs.map(m => m.approval!.title)
      const listing = titles.length === 1
        ? `"${titles[0]}"`
        : titles.map(t => `"${t}"`).join(', ')

      updated.push({
        id: `msg-${Date.now()}-decline`,
        type: 'assistant',
        content: `Done — I've declined ${pendingMsgs.length === 1 ? 'the pending approval' : `all ${pendingMsgs.length} pending approvals`}: ${listing}.`,
        timestamp: new Date().toISOString(),
        displayTime: formatDisplayTime(),
      })

      // Also call the API for each (fire-and-forget)
      for (const msg of pendingMsgs) {
        const approvalId = msg.approval?.id
        if (approvalId) api.denyAction(approvalId).catch(() => {})
      }

      return updated
    })

    return handled
  }, [setMessages])
}
